import { format } from "date-fns";
import { PricingSettings, DailySchedule, Closure } from "../types";
import { calculatePrice, calculateRequiredLanes, weekdayOf } from "./pricing";

/**
 * Központi árazási és nyitvatartás kezelő szolgáltatás
 * 
 * Ez a szolgáltatás egységesíti az árazási és nyitvatartási logikát,
 * amely korábban több helyen volt szétszórva (BookingForm, EditBookingForm, PricingSettings).
 */
class PricingService {
  private pricingData: PricingSettings | null = null;
  private listeners: Set<() => void> = new Set();

  /**
   * Beérkezett árak eltárolása (a usePricingService hook hívja a settings/pricing figyelőjéből).
   * Frissíti a cache-t és értesíti a feliratkozókat.
   */
  applyPricing(data: PricingSettings): PricingSettings {
    this.pricingData = data;
    this.notifyListeners();
    return data;
  }

  /**
   * Jelenlegi árazási adatok lekérdezése (cache-ből)
   */
  getPricing(): PricingSettings | null {
    return this.pricingData;
  }

  /**
   * Listener hozzáadása az árazási adatok változásaira
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Listener-ek értesítése a változásról
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Napi nyitvatartás lekérdezése egy adott dátumra
   * Figyelembe veszi a kivételeket (zárások és kinyitások)
   */
  getDaySchedule(date: Date): {
    isOpen: boolean;
    openingHour: number;
    closingHour: number;
    schedule: DailySchedule;
    hasException: boolean;
    exceptions: Closure[];
  } {
    if (!this.pricingData) {
      throw new Error("Árazási adatok még nem töltődtek be - a usePricingService hook tölti be őket a settings/pricing figyelőjéből.");
    }

    const daySchedule = this.pricingData[weekdayOf(date)];
    
    // Ellenőrizzük a kivételeket az adott napra
    const dateString = format(date, "yyyy-MM-dd");
    const closures = (this.pricingData.closures || []).filter(closure => 
      closure.date === dateString && (closure.type || 'closure') === 'closure'
    );
    const openings = (this.pricingData.closures || []).filter(closure => 
      closure.date === dateString && (closure.type || 'closure') === 'opening'
    );
    
    let openingHour = daySchedule.openingHour;
    let closingHour = daySchedule.closingHour;
    let isOpen = daySchedule.isOpen;
    
    // Kinyitások feldolgozása
    openings.forEach(opening => {
      if (opening.startTime && opening.endTime) {
        // Teljes felülírás konkrét időpontokkal
        const openingStartHour = parseInt(opening.startTime.split(":")[0]);
        let openingEndHour = parseInt(opening.endTime.split(":")[0]);
        
        if (openingEndHour >= 0 && openingEndHour < 6) {
          openingEndHour += 24;
        }
        
        openingHour = openingStartHour;
        closingHour = openingEndHour;
        isOpen = true;
      } else {
        // Részleges felülírás vagy kiterjesztés
        if (opening.startTime) {
          const openingStartHour = parseInt(opening.startTime.split(":")[0]);
          if (openingStartHour < openingHour) {
            openingHour = openingStartHour;
          }
        }
        
        if (opening.endTime) {
          let openingEndHour = parseInt(opening.endTime.split(":")[0]);
          if (openingEndHour >= 0 && openingEndHour < 6) {
            openingEndHour += 24;
          }
          if (openingEndHour > closingHour) {
            closingHour = openingEndHour;
          }
        }
        
        isOpen = true;
      }
    });
    
    // Zárások feldolgozása - ha egyébként nyitva lenne
    if (isOpen && closures.length > 0) {
      // Ellenőrizzük, hogy van-e egész napos zárás (nincs startTime és endTime)
      const fullDayClosures = closures.filter(c => !c.startTime && !c.endTime);
      
      // Ha van egész napos zárás, akkor az egész nap zárva
      if (fullDayClosures.length > 0) {
        isOpen = false;
      }
    }
    
    // Zárások csak akkor érvényesek, ha egyébként nyitva lenne
    if (!daySchedule.isOpen && openings.length === 0) {
      isOpen = false;
    }
    
    return {
      isOpen,
      openingHour,
      closingHour,
      schedule: daySchedule,
      hasException: closures.length > 0 || openings.length > 0,
      exceptions: [...closures, ...openings]
    };
  }

  /**
   * Ár kiszámítása egy foglaláshoz
   */
  calculateBookingPrice(
    date: Date,
    startTime: string,
    endTime: string,
    laneCount: number,
    shoesRental: number
  ): number {
    if (!this.pricingData) {
      throw new Error("Árazási adatok még nem töltődtek be.");
    }

    return calculatePrice(
      date,
      startTime,
      endTime,
      laneCount,
      shoesRental,
      this.pricingData
    );
  }

  /**
   * Szükséges pályák számának kiszámítása a vendégszám alapján
   */
  getRequiredLanes(date: Date, guestCount: number): number {
    if (!this.pricingData) {
      throw new Error("Árazási adatok még nem töltődtek be.");
    }

    const daySchedule = this.getDaySchedule(date);
    
    return calculateRequiredLanes(guestCount, {
      minGuestsPerLane: daySchedule.schedule.minGuestsPerLane,
      maxGuestsPerLane: daySchedule.schedule.maxGuestsPerLane
    });
  }

  /**
   * Ellenőrzi, hogy egy adott nap választható-e foglaláshoz
   */
  isDateSelectable(date: Date): boolean {
    if (!this.pricingData) {
      return false;
    }

    const daySchedule = this.getDaySchedule(date);
    return daySchedule.isOpen;
  }

  /**
   * Vendégszám korlátozások lekérdezése egy adott napra
   */
  getGuestLimits(date: Date): {
    minGuestsPerLane: number;
    maxGuestsPerLane: number;
    minTotal: number;
    maxTotal: number;
  } {
    if (!this.pricingData) {
      throw new Error("Árazási adatok még nem töltődtek be.");
    }

    const daySchedule = this.getDaySchedule(date);
    
    return {
      minGuestsPerLane: daySchedule.schedule.minGuestsPerLane,
      maxGuestsPerLane: daySchedule.schedule.maxGuestsPerLane,
      minTotal: daySchedule.schedule.minGuestsPerLane,
      maxTotal: daySchedule.schedule.maxGuestsPerLane * 4
    };
  }
}

// Singleton példány a globális használathoz
export const pricingService = new PricingService();

